/**
 * Converts an RTF string to plain text.
 * Returns the original string unchanged if it is not an RTF document.
 *
 * @param {string} rtf - The RTF string to convert.
 * @returns {string} The extracted plain text.
 */
export function rtfToText(rtf) {
  if (!rtf || typeof rtf !== "string") return rtf ?? "";
  if (!rtf.startsWith("{\\rtf")) return rtf;

  // Destination words whose content should be ignored entirely
  const SKIP_DESTINATIONS = new Set([
    "fonttbl",
    "colortbl",
    "stylesheet",
    "info",
    "listoverridetable",
    "listtable",
    "pnseclvl",
    "defchp",
    "deftab",
    "revtbl",
    "rsidtbl",
    "mmathPr",
    "themedata",
    "colorschememapping",
    "xmlnstbl",
    "latentstyles",
  ]);

  let dest = "";
  let i = 0;
  const stack = [];
  // state: skip – ignore text output; uc – number of ANSI chars after \uNNN to skip
  let state = { skip: false, uc: 1 };
  let skipChars = 0;

  while (i < rtf.length) {
    const ch = rtf[i];

    if (ch === "{") {
      stack.push({ ...state });
      i++;
    } else if (ch === "}") {
      state = stack.length > 0 ? stack.pop() : { skip: false, uc: 1 };
      i++;
    } else if (ch === "\\") {
      i++;
      if (i >= rtf.length) break;

      const next = rtf[i];

      // Hex-encoded character: \'xx
      if (next === "'") {
        const hex = rtf.substring(i + 1, i + 3);
        if (!state.skip) {
          if (skipChars > 0) {
            skipChars--;
          } else {
            dest += String.fromCharCode(parseInt(hex, 16));
          }
        }
        i += 3;

      // Control word: \word[-?digits][ ]
      } else if (/[a-zA-Z]/.test(next)) {
        const wordStart = i;
        while (i < rtf.length && /[a-zA-Z]/.test(rtf[i])) i++;
        const word = rtf.substring(wordStart, i);

        // Optional numeric argument (may be negative)
        const argStart = i;
        if (i < rtf.length && rtf[i] === "-") i++;
        while (i < rtf.length && /[0-9]/.test(rtf[i])) i++;
        const argStr = rtf.substring(argStart, i);
        const arg = argStr !== "" && argStr !== "-" ? parseInt(argStr, 10) : null;

        // Consume optional trailing space delimiter
        if (i < rtf.length && rtf[i] === " ") i++;

        // Handle known control words
        if (word === "uc") {
          state.uc = arg ?? 1;
        } else if (word === "u") {
          if (!state.skip) {
            // Unicode code point (may be signed 16-bit)
            const code = arg < 0 ? arg + 65536 : arg;
            dest += String.fromCodePoint(code);
            skipChars = state.uc; // skip the ANSI fallback char(s) that follow
          }
        } else if (word === "par" || word === "line") {
          if (!state.skip) dest += "\n";
        } else if (word === "tab") {
          if (!state.skip) dest += "\t";
        } else if (SKIP_DESTINATIONS.has(word)) {
          state.skip = true;
        }

      // \* – marks an optional destination that readers can ignore
      } else if (next === "*") {
        state.skip = true;
        i++;

      // Escaped special characters: \\  \{  \}
      } else if (next === "\\" || next === "{" || next === "}") {
        if (!state.skip) dest += next;
        i++;

      // Non-breaking space, optional hyphen, non-breaking hyphen
      } else if (next === "~") {
        if (!state.skip) dest += "\u00A0";
        i++;
      } else if (next === "-" || next === "_") {
        if (!state.skip) dest += "-";
        i++;

      // Any other single-char control symbol – skip
      } else {
        i++;
      }

    // Regular character
    } else {
      if (ch !== "\r" && ch !== "\n") {
        if (!state.skip) {
          if (skipChars > 0) {
            skipChars--;
          } else {
            dest += ch;
          }
        }
      }
      i++;
    }
  }

  return dest.trim();
}
